import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS } from '@/data/site';

const FAQS = [
  {
    q: 'Where is Majestic Greens DMV located?',
    a: `Majestic Greens DMV is located at ${BUSINESS.address}, ${BUSINESS.city}, ${BUSINESS.state} ${BUSINESS.zip} — in Downtown DC, on the edge of Penn Quarter and Chinatown, a short walk from the Gallery Place–Chinatown Metro station and Capital One Arena.`,
  },
  {
    q: 'What are your store hours?',
    a: `We are open daily from 10:00 AM to 11:00 PM. If you are planning a late visit, call ${BUSINESS.phone} to confirm same-day availability.`,
  },
  {
    q: 'What products do you carry?',
    a: 'Our live menu includes exotic and indoor cannabis flower, pre-rolls, vape cartridges, disposable vapes, edibles, concentrates, shake, and accessories. Browse the full menu online — it always reflects current stock.',
  },
  {
    q: 'How can I place an order?',
    a: 'Browse the menu, add products to your cart, choose pickup or delivery, upload your ID, and check out. Our team verifies your order and confirms before fulfillment.',
  },
  {
    q: 'Do I need identification?',
    a: 'Yes. All customers must be 21 years of age or older and present a valid government-issued photo ID. ID verification is part of checkout for every order.',
  },
  {
    q: 'Do you offer pickup?',
    a: `Yes — choose pickup at checkout and collect your order at ${BUSINESS.address}, ${BUSINESS.city}, ${BUSINESS.state} ${BUSINESS.zip} after verification.`,
  },
  {
    q: 'Do you offer delivery?',
    a: 'Yes — we deliver across Washington, DC. The minimum order is $40. Use our service area checker to confirm your address before ordering.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'Orders are paid electronically at checkout. Cash is accepted for pickup orders only — cash is not accepted for delivery orders.',
  },
  {
    q: 'How can I contact the store?',
    a: `Call us at ${BUSINESS.phone}. You can also use the contact page to send a message — our team responds during store hours.`,
  },
  {
    q: 'Where can I find your current menu?',
    a: 'The full live menu is on our shop page — flower, pre-rolls, vapes, edibles, concentrates and more, with current pricing and availability.',
  },
];

export default function Faq() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://majesticgreensdmv.com/' },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: 'https://majesticgreensdmv.com/faq' },
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Frequently Asked Questions | Majestic Greens DMV — Washington, DC"
        description={`Answers about Majestic Greens DMV in Washington, DC — location, hours, ordering, ID requirements, pickup, delivery, and payment. ${BUSINESS.address}. Adults 21+.`}
        path="/faq"
        jsonLd={jsonLd}
      />
      <div data-testid="faq-page" className="pb-28 pt-28">
        <div className="grain relative overflow-hidden border-b border-ink/10 bg-parchment">
          <div className="relative mx-auto max-w-4xl px-6 py-16">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-bronze">
                <Link to="/" data-testid="faq-breadcrumb-home" className="hover:text-bronzehover">Home</Link>
                <span className="text-ash"> / FAQ</span>
              </p>
              <h1 className="mt-4 font-serif text-4xl text-ink sm:text-6xl">Frequently asked questions</h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-sage">
                Everything customers ask us most about ordering from Majestic Greens DMV in Washington, DC — location, hours, products, pickup, delivery, and payment.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-6">
          <Reveal delay={0.05}>
            <Accordion type="single" collapsible className="mt-12">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-ink/10">
                  <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left font-serif text-lg text-ink hover:text-bronze">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-sage">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-col items-center border border-gold/30 bg-gold/5 p-10 text-center">
              <h2 className="font-serif text-3xl text-ink">Ready to browse the menu?</h2>
              <p className="mt-3 max-w-md text-sm text-sage">${BUSINESS.minOrder} minimum · Adults 21+ · {BUSINESS.hours}</p>
              <Link
                to="/shop"
                data-testid="faq-shop-cta"
                className="group mt-8 inline-flex items-center gap-3 bg-gold px-10 py-5 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bronzehover"
              >
                Shop the Menu <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href={BUSINESS.phoneHref} data-testid="faq-phone-cta" className="mt-4 text-sm font-semibold text-bronze underline underline-offset-4">
                Or call {BUSINESS.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
